import { stripHtmlToPlainText } from '@/lib/landing-html'
import type { LandingCampaign, LandingInstitution } from '@/types/landing'

export type LandingAccountVisualSource = Pick<
  LandingCampaign,
  | 'name'
  | 'title'
  | 'prize_description'
  | 'main_image_url'
  | 'public_share_image_url'
  | 'template3_hero_background_url'
  | 'campaign_images'
>

const PRIZE_LINE_MAX_LENGTH = 110

function firstNonEmptyUrl(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) {
      return trimmed
    }
  }

  return null
}

export function resolveLandingAccountBackdropUrl(
  campaign: LandingAccountVisualSource | null | undefined
): string | null {
  if (!campaign) {
    return null
  }

  return firstNonEmptyUrl(
    campaign.template3_hero_background_url,
    campaign.main_image_url,
    campaign.campaign_images?.[0]?.image_url,
    campaign.public_share_image_url
  )
}

export function resolveLandingAccountTitle(
  campaign: LandingAccountVisualSource | null | undefined
): string {
  if (!campaign) {
    return ''
  }

  return stripHtmlToPlainText(campaign.title) || campaign.name.trim() || ''
}

export function resolveLandingAccountPrizeLine(
  campaign: LandingAccountVisualSource | null | undefined
): string | null {
  if (!campaign) {
    return null
  }

  const text = stripHtmlToPlainText(campaign.prize_description)
  if (!text) {
    return null
  }

  if (text.length <= PRIZE_LINE_MAX_LENGTH) {
    return text
  }

  return `${text.slice(0, PRIZE_LINE_MAX_LENGTH - 1).trimEnd()}…`
}

export function buildLandingInstitutionName(
  institution: Pick<LandingInstitution, 'person_type' | 'corporate_name' | 'name' | 'first_name' | 'last_name'> | null | undefined
): string {
  if (!institution) {
    return ''
  }

  if (institution.person_type === 'pj') {
    return institution.corporate_name || institution.name
  }

  return institution.name || `${institution.first_name || ''} ${institution.last_name || ''}`.trim()
}
